import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg, itDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, MagnifyingGlass } from "@phosphor-icons/react";

const n0 = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const n2 = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);
const LINE_OPTS = [["SUINO", "Linea Suino"], ["BOVINO", "Linea Bovino"],
                   ["CRT", "Cartonato"], ["RILAVORAZIONE", "Rilavorazioni"]];

const quotaPeso = (r, operatorId) => {
  const w = Number(r.weight || 0);
  if (!operatorId || operatorId === "all") return w;
  const ops = r.operators || [];
  const mio = ops.find((o) => o.employee_id === operatorId);
  if (!mio) return 0;
  const pct = Number(mio.percent ?? (ops.length ? 100 / ops.length : 100));
  return w * pct / 100;
};

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-sm border border-border bg-card p-3" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-head font-bold tabular-nums">{value}</div>
  </div>
);

export default function WeightsView() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [operatorId, setOperatorId] = useState("all");
  const [line, setLine] = useState("all");
  const [client, setClient] = useState("all");
  const [q, setQ] = useState("");
  const [operators, setOperators] = useState([]);
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/weights/operators").then((r) => setOperators(r.data)).catch(() => {});
    api.get("/weights/meta").then((r) => setClients(r.data.clients || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/weights/entries", {
        params: { date_from: from, date_to: to, operator_id: operatorId, line, client, q },
      });
      setRows(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [from, to, operatorId, line, client, q]);
  useEffect(() => { load(); }, [load]);

  const perCottimista = operatorId !== "all";

  // i totali seguono sempre i record visibili in tabella; con un cottimista selezionato
  // il peso è la sua quota di divisione (i pezzi restano quelli della lavorazione)
  const tot = useMemo(() => rows.reduce((s, r) => ({
    weight: s.weight + Math.round(quotaPeso(r, operatorId) * 100) / 100,
    pieces: s.pieces + Number(r.pieces || 0),
  }), { weight: 0, pieces: 0 }), [rows, operatorId]);

  return (
    <div className="space-y-3" data-testid="weights-view-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Consultazione Pesi</h1>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
          <Eye size={15} /> Sola lettura: filtra le pesate per data, cottimista, linea e cliente.
        </p>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center"
        data-testid="wv-filters">
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          className="h-9 w-[150px] rounded-sm" data-testid="wv-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          className="h-9 w-[150px] rounded-sm" data-testid="wv-to" />
        <Select value={operatorId} onValueChange={setOperatorId}>
          <SelectTrigger className="h-9 w-[220px] rounded-sm" data-testid="wv-operator"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tutti i cottimisti</SelectItem>
            {operators.map((o) => <SelectItem key={o.employee_id} value={o.employee_id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={line} onValueChange={setLine}>
          <SelectTrigger className="h-9 w-[220px] rounded-sm" data-testid="wv-line"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le linee</SelectItem>
            {LINE_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={client} onValueChange={setClient}>
          <SelectTrigger className="h-9 w-[220px] rounded-sm" data-testid="wv-client"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tutti i clienti</SelectItem>
            {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca sigla, descrizione, cliente..."
            className="h-9 w-64 pl-8 rounded-sm" data-testid="wv-search" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Pesate" value={rows.length} testid="wv-kpi-rows" />
        <Kpi label="Pezzi" value={n0(tot.pieces)} testid="wv-kpi-pieces" />
        <Kpi label={perCottimista ? "Peso in quota" : "Peso totale"} value={n2(tot.weight)} testid="wv-kpi-weight" />
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm tbl-sticky" data-testid="wv-table">
          <thead className="bg-secondary"><tr>
            {["Data", "Sigla", "Lavorazione", "Cliente", "Linea", "Pezzi", "Peso",
              ...(perCottimista ? ["Quota kg"] : []), "Cottimisti"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`wv-row-${r.id}`}>
                <td className="px-3 py-1.5 whitespace-nowrap">{itDate(r.date)}</td>
                <td className="px-3 py-1.5 font-semibold">{r.code || "—"}</td>
                <td className="px-3 py-1.5">{r.description}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.client || "—"}</td>
                <td className="px-3 py-1.5">{r.line}</td>
                <td className="px-3 py-1.5 tabular-nums">{n0(r.pieces)}</td>
                <td className="px-3 py-1.5 tabular-nums">{n2(r.weight)}</td>
                {perCottimista && (
                  <td className="px-3 py-1.5 tabular-nums font-semibold" data-testid={`wv-quota-${r.id}`}>
                    {n2(quotaPeso(r, operatorId))}
                  </td>
                )}
                <td className="px-3 py-1.5 text-xs text-muted-foreground">
                  {(r.operators || []).map((o) => o.name).filter(Boolean).join(", ") || "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={perCottimista ? 9 : 8} className="px-3 py-8 text-center text-muted-foreground" data-testid="wv-empty">
                Nessuna pesata con i filtri selezionati
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
